import * as R from 'ramda';
import { createAction } from 'redux-actions';
import { REWARD_CONTEXTS, GLOBAL_KEYWORD } from '@trs/config';
import { GET, POST, PUT, CancelToken, addGetParameters } from 'config/http';
import { rewards, bonusModellerApi } from 'config/api';
import { withCache } from 'modules/common/timedCache';
import { dispatchError } from 'modules/common/helpers';
import { validatePopulations, validateRules, validateDisclaimer, hasErrors } from '../validation';
import { mapBonusModellerResponse } from '../payload';
import { dispatchError as dispatchRequestError } from './helpers';

const ROOT_TYPE = 'BONUS_MODELLER';

export const actionTypes = {
  GET_ELIGIBILITY_OPERATORS: `${ROOT_TYPE}/GET_ELIGIBILITY_OPERATORS`,
  GET_SYSTEM_VARIABLES: `${ROOT_TYPE}/GET_SYSTEM_VARIABLES`,
  GET_SYSTEM_VARIABLES_LOOKUP_DATA: `${ROOT_TYPE}/GET_SYSTEM_VARIABLES_LOOKUP_DATA`,
  GET_FORMULA_OPERATORS: `${ROOT_TYPE}/GET_FORMULA_OPERATORS`,
  POPULATION_CHANGE: `${ROOT_TYPE}/POPULATION_CHANGE`,
  RULE_CHANGE: `${ROOT_TYPE}/RULE_CHANGE`,
  ADD_RULE_BLOCK: `${ROOT_TYPE}/ADD_RULE_BLOCK`,
  DUPLICATE_RULE_BLOCK: `${ROOT_TYPE}/DUPLICATE_RULE_BLOCK`,
  DELETE_RULE_BLOCK: `${ROOT_TYPE}/DELETE_RULE_BLOCK`,
  DISCLAIMER_FIELD_CHANGE: `${ROOT_TYPE}/DISCLAIMER_FIELD_CHANGE`,
  SET_ERRORS: `${ROOT_TYPE}/SET_ERRORS`,
  RESET_POPULATIONS: `${ROOT_TYPE}/RESET_POPULATIONS`,
  RESET_RULES: `${ROOT_TYPE}/RESET_RULES`,
  STORE_RESET: `${ROOT_TYPE}/STORE_RESET`,
  SAVE: `${ROOT_TYPE}/SAVE`,
  ETAG_UPDATE: `${ROOT_TYPE}/ETAG_UPDATE`,
  PUBLISHED: `${ROOT_TYPE}/PUBLISHED`,
  GET_BY_ID: `${ROOT_TYPE}/GET_BY_ID`,
};

export const getEligibilityOperators = createAction(actionTypes.GET_ELIGIBILITY_OPERATORS);
export const getSystemVariables = createAction(actionTypes.GET_SYSTEM_VARIABLES);
export const getVariablesLookupData = createAction(actionTypes.GET_SYSTEM_VARIABLES_LOOKUP_DATA);
export const getFormulaOperators = createAction(actionTypes.GET_FORMULA_OPERATORS);
export const populationChange = createAction(actionTypes.POPULATION_CHANGE);
export const ruleChange = createAction(actionTypes.RULE_CHANGE);
export const addRuleBlockAction = createAction(actionTypes.ADD_RULE_BLOCK, (rulesKey, index) => ({
  rulesKey,
  index,
}));
export const duplicateRuleBlockAction = createAction(
  actionTypes.DUPLICATE_RULE_BLOCK,
  (rulesKey, index, rule) => ({ rulesKey, index, rule })
);
export const deleteRuleBlockAction = createAction(
  actionTypes.DELETE_RULE_BLOCK,
  (rulesKey, index) => ({ rulesKey, index })
);
export const disclaimerFieldChange = createAction(
  actionTypes.DISCLAIMER_FIELD_CHANGE,
  (field, value) => ({ field, value })
);
export const setErrors = createAction(actionTypes.SET_ERRORS, (field, value) => ({
  field,
  value,
}));
export const resetPopulations = createAction(actionTypes.RESET_POPULATIONS);
export const resetRules = createAction(actionTypes.RESET_RULES);
export const storeReset = createAction(actionTypes.STORE_RESET);
export const bonusModellerSave = createAction(actionTypes.SAVE, (response, id) => ({
  response,
  id,
}));
export const etagUpdate = createAction(actionTypes.ETAG_UPDATE, (etag) => ({ etag }));
export const bonusModellerPublished = createAction(actionTypes.PUBLISHED);
export const bonusModellerLoaded = createAction(actionTypes.GET_BY_ID, (data, id) => ({
  ...data,
  id,
}));

const cachedGet = withCache(GET, 3600);

export const loadEligibilityOperators = () => (dispatch) =>
  cachedGet(rewards.eligibilityOperators).then((response) =>
    dispatch(getEligibilityOperators(R.prop('data', response)))
  );

export const loadSystemVariables = () => (dispatch) =>
  cachedGet(rewards.systemVariables, { Pragma: 'no-cache' }).then((response) =>
    dispatch(getSystemVariables(R.prop('data', response)))
  );

export const loadSystemVariablesLookup = (url) => (dispatch) =>
  cachedGet(`${rewards.systemVariables}/${url}`)
    .then((response) =>
      dispatch(getVariablesLookupData({ lookupData: R.prop('data', response), url }))
    )
    .catch((e) => dispatchError(e, dispatch));

export const loadFormulaOperators = () => (dispatch) =>
  cachedGet(rewards.formulaOperators, { Pragma: 'no-cache' }).then((response) =>
    dispatch(getFormulaOperators(R.prop('data', response)))
  );

export const loadDependencies = () => (dispatch) =>
  Promise.all([
    dispatch(loadSystemVariables()),
    dispatch(loadEligibilityOperators()),
    dispatch(loadFormulaOperators()),
  ]).catch((e) => dispatchError(e, dispatch));

export const validatePopulationsStep = () => (dispatch, getState) => {
  const { populations } = getState().onboarding.bonusModeller;
  const errors = validatePopulations(populations);
  dispatch(setErrors('populations', errors));
  return !hasErrors(errors);
};

export const validateRulesStep = (rulesKey) => (dispatch, getState) => {
  const rules = getState().onboarding.bonusModeller[rulesKey];
  const errors = validateRules(rules);
  dispatch(setErrors(rulesKey, errors));
  return !hasErrors(errors);
};

export const validateDisclaimerStep = () => (dispatch, getState) => {
  const { includeDisclaimer, disclaimer } = getState().onboarding.bonusModeller;
  const error = validateDisclaimer({ includeDisclaimer, disclaimer });
  dispatch(setErrors('disclaimer', error));
  return !error;
};

// Validation only runs on Save/Publish (see validate*Step above). Editing a step clears any
// previously shown errors for it, since they may no longer match the current data, and they
// are recomputed the next time Save/Publish is clicked.
export const changePopulationField = (index, field, value, other) => (dispatch) => {
  dispatch(populationChange({ index, field, value, other }));
  dispatch(setErrors('populations', []));
};

export const changeRuleField = (rulesKey, index, field, value, other) => (dispatch) => {
  dispatch(ruleChange({ rulesKey, index, field, value, other }));
  dispatch(setErrors(rulesKey, []));
};

export const addRuleBlock = (rulesKey, index) => (dispatch) => {
  dispatch(addRuleBlockAction(rulesKey, index));
  dispatch(setErrors(rulesKey, []));
};

export const duplicateRuleBlock = (rulesKey, index, rule) => (dispatch) => {
  dispatch(duplicateRuleBlockAction(rulesKey, index, rule));
  dispatch(setErrors(rulesKey, []));
};

export const deleteRuleBlock = (rulesKey, index) => (dispatch) => {
  dispatch(deleteRuleBlockAction(rulesKey, index));
  dispatch(setErrors(rulesKey, []));
};

export const changeDisclaimerField = (field, value) => (dispatch) => {
  dispatch(disclaimerFieldChange(field, value));
  dispatch(setErrors('disclaimer', ''));
};

// response may be wrapped as {data:{response:{id}}} or returned flat as {data:{id}}
const extractSavedId = (response) =>
  R.path(['data', 'response', 'id'], response) || R.path(['data', 'id'], response);

const extractSaveEtag = (response) => R.path(['data', 'eTag'], response);

const dispatchSavedRecord = (dispatch, response, fallbackId) => {
  dispatch(bonusModellerSave(R.prop('data', response), extractSavedId(response) || fallbackId));
  dispatch(etagUpdate(extractSaveEtag(response)));
};

export const saveBonusModellerData = (payload, id, config = {}) => {
  if (!id) {
    return (dispatch, getState) =>
      POST(bonusModellerApi.save, payload, {}, { redirectToUnauthorized: false })
        .then((response) => {
          dispatchSavedRecord(dispatch, response);
          return response;
        })
        .catch((error) => {
          dispatchRequestError(error, dispatch, getState());
          return Promise.reject(error);
        });
  }

  return (dispatch, getState) =>
    PUT(
      bonusModellerApi.saveById(id),
      payload,
      { ...config.headers },
      { redirectToUnauthorized: false }
    )
      .then((response) => {
        dispatchSavedRecord(dispatch, response, id);
        return response;
      })
      .catch((error) => {
        dispatchRequestError(error, dispatch, getState());
        return Promise.reject(error);
      });
};

export const publishBonusModellerData = (id, config = {}) => (dispatch, getState) =>
  PUT(
    bonusModellerApi.publish(id),
    {},
    { ...config.headers },
    { redirectToUnauthorized: false }
  )
    .then((response) => {
      dispatch(bonusModellerPublished(R.prop('data', response)));
      dispatch(etagUpdate(R.path(['data', 'eTag'], response)));
      return response;
    })
    .catch((error) => {
      dispatchRequestError(error, dispatch, getState());
      return Promise.reject(error);
    });

export const loadBonusModellerById = (id) => (dispatch, getState) =>
  GET(bonusModellerApi.getById(id), { Pragma: 'no-cache' })
    .then((response) => {
      // Response structure: { data: { errors, isSuccessful, response, eTag }, status, headers, ... }
      const data = R.path(['data', 'response'], response);
      if (!data) return null;
      const etagValue = R.path(['data', 'eTag'], response);
      const mappedData = mapBonusModellerResponse(data);
      dispatch(bonusModellerLoaded(mappedData, R.prop('id', data) || id));
      dispatch(etagUpdate(etagValue));
      return data;
    })
    .catch((error) => {
      dispatchRequestError(error, dispatch, getState());
      return Promise.reject(error);
    });

// list payload may be an array, or wrapped under response / data.response / data.response.items
const extractBonusModellerList = (response) => {
  const candidates = [
    R.prop('response', response), // top-level response array
    R.path(['data', 'response', 'items'], response),
    R.path(['data', 'response'], response),
    R.path(['data', 'items'], response),
    R.prop('data', response),
  ];

  return candidates.find(Array.isArray) || [];
};

// a tenant has at most one bonus modeller record, so the first entry is the one to edit
export const loadExistingBonusModeller = () => (dispatch, getState) =>
  GET(bonusModellerApi.allBonus, { Pragma: 'no-cache' })
    .then((response) => {
      const [record] = extractBonusModellerList(response);
      
      const existingId = R.prop('id', record);

      if (!existingId) return null;

      dispatch(bonusModellerLoaded(mapBonusModellerResponse(record), existingId));
      dispatch(etagUpdate(R.prop('eTag', record) || R.path(['data', 'eTag'], response)));
      return record;
    })
    .catch((error) => {
      dispatchRequestError(error, dispatch, getState());
      return Promise.reject(error);
    });

export const revertBonusModellerToDraft = (id, config = {}) => (dispatch, getState) =>
  PUT(
    bonusModellerApi.moveToDraft(id),
    {},
    { ...config.headers },
    { redirectToUnauthorized: false }
  )
    .then((response) => dispatch(loadBonusModellerById(id)).then(() => response))
    .catch((error) => {
      dispatchRequestError(error, dispatch, getState());
      return Promise.reject(error);
    });

    
export const getFormulaOperandValues = (resources) => () => {
  let cancelOperandCall;
  return (searchValue) => {
    if (!searchValue) return Promise.resolve([]);

    if (cancelOperandCall) cancelOperandCall.cancel('Formula Operand call cancelled.');
    cancelOperandCall = CancelToken.source();

    const requestUrl = addGetParameters(
      `${rewards.formulaOperand}/${encodeURIComponent(searchValue)}`,
      [
        { name: 'resourceCountry', value: R.propOr(GLOBAL_KEYWORD, 'resourceCountry', resources) },
        { name: 'context', value: REWARD_CONTEXTS.reward },
      ]
    );

    return GET(requestUrl, { cancelToken: cancelOperandCall.token }).then((response) =>
      R.reject(R.propEq('isAmount', false), R.prop('data', response))
    );
  };
};
